import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import Button from '../Button/Button';
import AnalyseOffreTechnique from '../AnalyseOffreTechnique/AnalyseOffreTechnique';
import SyntheseAnalyse from '../SyntheseAnalyse/SyntheseAnalyse';
import AnalyseOffreFinanciere from '../AnalyseOffreFinanciere/AnalyseOffreFinanciere';
import DetailsNotesModal from '../DetailsNotesModal/DetailsNotesModal';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import { pdf } from '@react-pdf/renderer';
import AnalyseRecapPDF from '../../pages/PDF/AnalyseRecapPDF';
import AnalysePrestatairePDF from '../../pages/PDF/AnalysePrestatairePDF';
import { useAuth } from '../../contexts/AuthContext';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import DropDown from '../DropDown/DropDown';
import './Analyse.css';
import { PrintButton } from '../PrintButton/PrintButton';
import { getLotImage } from '../../utils/getLotImage';
import { getStatusColor } from '../../utils/entityUtils';
import { isPrestataireRole } from '../../utils/roleUtils';

interface AnalyseProps {
  commande: any;
  techNotes: {
    [prestaId: string]: {
      [critere: string]: {
        note?: number;
        commentaire?: string;
        description?: string;
      }
    }
  };
  setTechNotes: React.Dispatch<React.SetStateAction<{
    [prestaId: string]: {
      [critere: string]: {
        note?: number;
        commentaire?: string;
        description?: string;
      }
    }
  }>>;
  selectedPrestataire: number | null;
  setSelectedPrestataire: React.Dispatch<React.SetStateAction<number | null>>;
  isDetailsModalOpen: boolean;
  setIsDetailsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleExport: () => void;
  calculateFinancialScore: (prestataires: any[]) => { [id: string]: number };
  getNoteGlobale: (techNotes: any, prestaId: string) => string;
  handleFinaliserCommande: (id_signataire?: number, type_signataire?: string) => void;
  viewOnly?: boolean;
  printRef?: any;
  // canClotureAnalyse?: boolean; // Ajout de cette propriété
}

const Analyse: React.FC<AnalyseProps> = ({
  commande,
  techNotes,
  setTechNotes,
  selectedPrestataire,
  setSelectedPrestataire,
  isDetailsModalOpen,
  setIsDetailsModalOpen,
  handleExport,
  calculateFinancialScore,
  getNoteGlobale,
  handleFinaliserCommande,
  viewOnly = false,
  printRef
  // canClotureAnalyse = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPrestaDetails, setSelectedPrestaDetails] = useState<any>(null);
  // Ajouter un état local pour gérer les modifications de la commande
  const [commandeState, setCommandeState] = useState(commande);
  // Nouvel état pour la modale de succès
  const [isClotureSuccessModalOpen, setIsClotureSuccessModalOpen] = useState(false);
  // Nouvel état pour la modale d'erreur
  const [clotureErrorModal, setClotureErrorModal] = useState<string | null>(null);

  // const contentRef = useRef<HTMLDivElement>(null);

  const isPrestataireView = isPrestataireRole(user?.role || '');


  const getCandidatureDate = (commandeId: string) => {
    if (!commande?.historique) {
      console.warn('Operation historique is missing');
      return null;
    }

    const entry = commande.historique.find(
      (h: any) =>
        h.prefix === 'Candidature' &&
        h.destinataire?.toString() === commandeId?.toString()
    );

    if (!entry) {
      console.warn(`No candidature entry found for commande ${commandeId}`);
      return null;
    }

    return entry.created_at;
  };
  // Fonction après confirmation du signataire
  const handleSignataireModalConfirm = async () => {
    if (!selectedSignataireId) {
      alert("Veuillez sélectionner un signataire avant de finaliser la commande.");
      return;
    }

    setIsSignataireModalOpen(false);

    // Récupérer l'ID et type du signataire
    const [id, type_signataire] = selectedSignataireId.split('|');

    try {
      await handleFinaliserCommande(parseInt(id), type_signataire);
      // Si l'API retourne OK, afficher la modale finale
      setIsClotureSuccessModalOpen(true);
    } catch (error: any) {
      // Si erreur, afficher modale d'erreur
      setClotureErrorModal(error?.message || "Une erreur est survenue lors de la clôture de l'analyse.");
    }
  };

  // Fermeture des modales finales
  const handleClotureSuccessContinue = () => {
    setIsClotureSuccessModalOpen(false);
    navigate(`/operation-ms/${commande.id}?tab=3`); // ou autre action après la confirmation
    window.location.reload();

  };
  const handleClotureErrorClose = () => setClotureErrorModal(null);

  // Vérifier si le statut est "Consultation en cours"
  if (commande.status && commande.status.id == 2) {
    return (
      <div className="tab-analyse">
        <div className="candidature-en-cours-container">
          <div className="candidature-en-cours">
            <div className="candidature-header">
              <i className="fas fa-hourglass-half"></i>
              <h3>Consultation en cours</h3>
            </div>
            <div className="candidature-content">
              <p>Les candidats et leurs offres apparaîtront dans cet onglet à l'issue du délai de consultation ou lorsque tous les candidats auront répondu.</p>
              <div>
                <span className="countdown-label">Echéance dans : </span>
                <CompteARebourd targetDate={commande.expire_at} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // État pour gérer le modal de confirmation de clôture d'analyse
  const [isClotureModalOpen, setIsClotureModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  // Ajouter un état pour gérer le modal de sélection de signataire
  const [isSignataireModalOpen, setIsSignataireModalOpen] = useState(false);

  // Effet pour initialiser les notes techniques à partir des commandes
  useEffect(() => {
    if (commande && commande.commandes && Array.isArray(commande.commandes)) {
      const techNotesInit = { ...techNotes };

      commande.commandes.forEach((cmd, index) => {
        if (cmd.note_technique) {
          // Pour les offres expirées (id_status=12), les commentaires ne sont pas requis
          const isExpired = cmd.id_status === 12;

          techNotesInit[index.toString()] = {
            "organisation": {
              note: cmd.note_technique.note_operationnel ?? 0,
              commentaire: cmd.note_technique.commentaire_operationnel || '',
              description: cmd.note_technique.description_operationnel || '',
              isExpired: isExpired
            },
            "moyens": {
              note: cmd.note_technique.note_humain ?? 0,
              commentaire: cmd.note_technique.commentaire_humain || '',
              description: cmd.note_technique.description_humain || '',
              isExpired: isExpired
            },
            "social": {
              note: cmd.note_technique.note_environnement ?? 0,
              commentaire: cmd.note_technique.commentaire_environnement || '',
              description: cmd.note_technique.description_environnement || '',
              isExpired: isExpired
            }
          };
        }
      });

      console.log("Notes techniques initialisées:", techNotesInit);
      setTechNotes(techNotesInit);
    }
  }, [commande, setTechNotes]);

  const isAnalyzed = commande.historique?.some((entry: any) => entry.prefix === 'Consulte' || entry.prefix === 'Consultation');

  const formatAnalyzeDate = (date: string) => {
    if (!date) return null;
    const dateObj = new Date(date);

    const datePart = dateObj.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const timePart = dateObj.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    return `${datePart} à ${timePart}`;
  };
  const dateAnalyzeLancee = commande.historique?.find((entry: any) => entry.prefix === 'Refusé' || entry.prefix === 'Validation')?.created_at;
  const formatedAnalyzeLancee = formatAnalyzeDate(dateAnalyzeLancee);
  const analyzeOperateur = commande.historique?.find((entry: any) => entry.prefix === 'Refusé' || entry.prefix === 'Validation')?.operateur || 'Inconnu';


  // Fonction pour ouvrir le modal de confirmation
  const handleClotureConfirm = () => {
    setIsClotureModalOpen(true);
  };

  // Fonction qui sera appelée après la confirmation de la clôture
  const handleClotureModalConfirm = async () => {
    setIsClotureModalOpen(false);
    setIsClosing(true); // Bloque le bouton

    try {
      await handleSignataireModalConfirm(); // ta fonction async qui clôture
    } catch (error) {
      console.error("Erreur lors de la clôture :", error);
    } finally {
      setIsClosing(false); // Débloque le bouton
    }
  };

  // // Fonction qui sera appelée après la confirmation du choix du signataire
  // const handleSignataireModalConfirm = () => {
  //   if (!selectedSignataireId) {
  //     alert("Veuillez sélectionner un signataire avant de finaliser la commande.");
  //     return;
  //   }

  //   setIsSignataireModalOpen(false);
  //   handleFinaliserCommandeWithSignataire();
  // };

  // Fonction pour générer le PDF récapitulatif global
  const generateGlobalPDF = async () => {
    try {
      // Préparer les données enrichies pour le PDF
      const pdfData = {
        ...commande,
        formatedAnalyzeLancee,
        analyzeOperateur,
        // Conserver les autres propriétés de la commande
      };

      // Générer le PDF avec les données enrichies
      const blob = await pdf(
        <AnalyseRecapPDF
          commande={pdfData}
          user={user}
          techNotes={techNotes}
          calculateFinancialScore={calculateFinancialScore}
          getNoteGlobale={getNoteGlobale}
          selectedPrestataire={selectedPrestataire}
        />
      ).toBlob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analyse-recap-${commande.numeroDeCommande || commande.id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    }
  };

  // Fonction pour générer le PDF d'un prestataire spécifique
  const generatePrestatairePDF = async (prestaId: number) => {
    try {
      console.log('Génération PDF pour prestataire ID:', prestaId);
      const blob = await pdf(
        <AnalysePrestatairePDF
          commande={commande}
          user={user}
          techNotes={techNotes}
          calculateFinancialScore={calculateFinancialScore}
          getNoteGlobale={getNoteGlobale}
          prestaId={prestaId} // Correction ici: passer prestaId et non commande.id
        />
      ).toBlob();

      // Récupérer le nom du prestataire à partir des commandes
      let orgName = "prestataire";
      if (commande.commandes && Array.isArray(commande.commandes)) {
        const prestataire = commande.commandes.find(cmd => cmd.id === prestaId) ||
          (prestaId < commande.commandes.length ? commande.commandes[prestaId] : null);
        if (prestataire) {
          orgName = prestataire.nom_entite || prestataire.nom || `prestataire-${prestaId}`;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analyse-${orgName}-${commande.numero || commande.id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    }
  };

  // Calculer les montants totaux par prestataire à partir des sections et articles
  const calculateTotalMontants = () => {
    const montants: { id: string, montant: number }[] = [];

    if (commande.commandes && Array.isArray(commande.commandes)) {
      commande.commandes.forEach((cmd, idx) => {
        let total = 0;
        if (cmd.sections && Array.isArray(cmd.sections)) {
          cmd.sections.forEach(section => {
            if (section.articles && Array.isArray(section.articles)) {
              section.articles.forEach(article => {
                if (article.prix) {
                  total += parseFloat(article.prix);
                }
              });
            }
          });
        }
        montants.push({
          id: idx.toString(),
          montant: total
        });
      });
    }

    return montants;
  };

  // Si la commande n'a pas de prestataires ou le tableau est vide, afficher un message
  const hasPrestataires = (commande.prestataires && commande.prestataires.length > 0) ||
    (commande.commandes && commande.commandes.length > 0);

  console.log("Vérification prestataires:", {
    hasPrestataires,
    commandes: commande.commandes,
    commandesLength: commande.commandes?.length,
    prestataires: commande.prestataires,
    prestataresLength: commande.prestataires?.length
  });

  if (!hasPrestataires) {
    return (
      <div className="tab-analyse">
        <div className="empty-state">
          <p>Aucun prestataire n'est disponible pour cette commande.</p>
        </div>
      </div>
    );
  }

  // Construire les données des prestataires à partir des commandes de l'API
  const buildPrestatairesData = () => {
    if (commande.commandes && Array.isArray(commande.commandes)) {

      return commande.commandes.map((cmd, idx) => {
        const date = new Date(cmd.updated_at || new Date());
        const dateReponse = getCandidatureDate(cmd.id.toString())
        const isExpired = cmd.id_status === 12;
        const responseText = cmd.reponse || cmd.reponse_technique || commande.reponse_technique;

        return {
          id: idx.toString(),
          presta_id: cmd.id,
          id_status: cmd.id_status,
          isExpired: isExpired, // Indicateur d'offre expirée
          organisme: cmd.contrat.entite.nom || `Prestataire ${idx + 1}`,
          // Pour les offres expirées, ajouter un indicateur visuel
          labelStatus: isExpired ? '(Expiré)' : '',
          offreTechnique: {
            // Si expiré ou pas de réponse, afficher "Offre non reçue"
            reponseTechnique: isExpired || !responseText ? "Offre non reçue" : responseText
          },
          dateReponse: dateReponse
        };
      });
    } else if (commande.prestataires && Array.isArray(commande.prestataires)) {
      return commande.prestataires.map((p, idx) => {
        const isExpired = p.id_status === 12;
        const responseText = p.responseTechnique;

        return {
          id: idx.toString(),
          id_status: p.id_status,
          isExpired: isExpired,
          organisme: p.organisme,
          labelStatus: isExpired ? '(Expiré)' : '',
          offreTechnique: {
            reponseTechnique: isExpired || !responseText ? "Offre non reçue" : responseText
          }
        };
      });
    }
    return [];
  };

  const getBestPrestataire = () => {
    if (!commande || !commande.commandes || commande.commandes.length === 0) return null;

    const financialScores = calculateFinancialScore(commande.commandes);

    // Ne considérer que les prestataires valides (non expirés)
    const prestatairesValides = commande.commandes.filter(p => p.id_status !== 12);
    if (prestatairesValides.length === 0) return null;

    let bestId = null;
    let bestScore = -1;

    prestatairesValides.forEach((cmd, index) => {
      // Trouver l'index réel dans le tableau commande.commandes
      const realIndex = commande.commandes.findIndex(c => c.id === cmd.id);
      if (realIndex === -1) return;

      const techScore = parseFloat(getNoteGlobale(techNotes, realIndex.toString()) || "0");
      const finScore = financialScores[realIndex.toString()] || 0;

      // Pondération : par exemple 70% technique, 30% financière
      const totalScore = techScore * 0.55 + finScore * 0.45;

      if (totalScore > bestScore) {
        bestScore = totalScore;
        bestId = cmd.id;
      }
    });

    return bestId;
  };

  // Préparer les données pour SyntheseAnalyse
  const prepareSyntheseData = () => {
    const montants = calculateTotalMontants();

    const prestatairesList = commande.commandes || commande.prestataires || [];
    return prestatairesList.map((p, idx) => {
      const id = idx.toString();
      const presta_id = p.id;

      // Note technique : utiliser les notes de l'utilisateur (techNotes) car elles peuvent être modifiées
      const techScore = techNotes[id] ? parseFloat(getNoteGlobale(techNotes, id)) : (p.note_technique?.note_technique ?? 0);

      // Note financière : toujours utiliser celle de l'API (elle est fixe)
      const finScore = p.note_financiere ?? 0;

      // Note totale : recalculer avec la pondération 55% technique + 45% financière
      const totalScore = (techScore * 0.55) + (finScore * 0.45);

      const montant = p.prix_total || (montants.find(m => m.id === id)?.montant || 0);
      const status = p.id_status;
      const isExpired = status === 12;
      const date = new Date(p.updated_at || new Date());
      const dateReponse = getCandidatureDate(p.id.toString())

      return {
        id: id,
        presta_id: presta_id,
        id_status: p.id_status,
        nom: p.contrat.entite.nom,
        // date au format JJ/MM/AAAA à hh:mm
        dateReponse: dateReponse,
        isExpired: isExpired, // Indique si l'offre est expirée
        offreTechnique: {
          note: isExpired ? 0 : techScore, // Note technique à 0 pour les offres expirées
          url: "#"
        },
        offreFinanciere: {
          montant: montant,
          note: isExpired ? 0 : finScore // Note financière à 0 pour les offres expirées
        },
        note_financiere: isExpired ? 0 : finScore,
        noteTotal: isExpired ? 0 : totalScore // Note totale à 0 pour les offres expirées
      };
    });
  };

  const [loading, setLoading] = useState(false);
  console.log("commande", commande)
  const isViewOnly = viewOnly || commande.status?.id !== 4;

  const handleSaveAndQuit = async () => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token non trouvé, veuillez vous reconnecter.');

    setLoading(true);

    try {
      console.log("=== DEBUG handleSaveAndQuit ===");
      console.log("Commandes disponibles:", commande.commandes);
      console.log("Status de l'opération:", commande.status);

      // Identifier le prestataire gagnant
      // Exclure les prestataires expirés
      const prestatairesValides = commande.commandes.filter(p => p.id_status !== 12);

      console.log("Prestataires valides (non expirés):", prestatairesValides);

      // Trouver le gagnant parmi les valides
      const gagnant = prestatairesValides.reduce((prev, current) =>
        (prev.noteTotal > current.noteTotal) ? prev : current
        , prestatairesValides[0]); // valeur initiale

      const gagnantId = gagnant?.id ?? null;
      console.log("Prestataire gagnant ID:", gagnantId);
      console.log("Meilleur prestataire (getBestPrestataire):", getBestPrestataire());

      // Construire le nouveau payload "commandes"
      const payload = {
        operation_id: commande.id,
        commandes: (commande.commandes || []).map((cmd, index) => {
          const notes = techNotes[index.toString()] || {};

          // Pour les offres expirées (id_status=12), on n'exige pas les commentaires/descriptions
          const isExpired = cmd.id_status === 12;

          return {
            commande_id: cmd.id,
            id_note_technique: cmd.id_note_technique,
            selected: cmd.id === getBestPrestataire(), // <-- true si c'est le gagnant
            note_operationnel: notes.organisation?.note ?? 0,
            commentaire_operationnel: isExpired ? "" : (notes.organisation?.commentaire ?? ""),
            description_operationnel: isExpired ? "" : (notes.organisation?.description ?? ""),
            note_humain: notes.moyens?.note ?? 0,
            commentaire_humain: isExpired ? "" : (notes.moyens?.commentaire ?? ""),
            description_humain: isExpired ? "" : (notes.moyens?.description ?? ""),
            note_environnement: notes.social?.note ?? 0,
            commentaire_environnement: isExpired ? "" : (notes.social?.commentaire ?? ""),
            description_environnement: isExpired ? "" : (notes.social?.description ?? ""),
          };
        }),
      };

      console.log("Payload complet à envoyer :", payload);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subsequents/operation/${commande.id}/note`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(`Erreur API: ${response.status} ${JSON.stringify(err)}`);
      }

      console.log("Toutes les notes ont été sauvegardées avec succès");
      navigate("/operations");
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
    } finally {
      setLoading(false);
    }
  };

  const analyseRefValidate = useRef<{ validate: () => boolean }>(null);


  // États pour la gestion des signataires
  const [signataires, setSignataires] = useState<any[]>([]);
  const [signatairesLoading, setSignatairesLoading] = useState<boolean>(false);
  const [signatairesError, setSignatairesError] = useState<string | null>(null);
  const [selectedSignataireId, setSelectedSignataireId] = useState<string | null>(null);
  const [signatairesOptions, setSignatairesOptions] = useState<{ label: string; value: string }[]>([]);


  // Mise à jour de l'état local quand commande change
  useEffect(() => {
    setCommandeState(commande);
  }, [commande]);

  // Fonction pour mettre à jour le signataire dans l'objet commande
  const updateCommandeSignataire = (id, type_signataire) => {
    setCommandeState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        id_signataire: id,
        type_signataire: type_signataire
      };
    });
  };

  // Charger les signataires depuis l'API
  useEffect(() => {
    if (!commandeState?.organisme?.id) return;

    const fetchSignataires = async () => {
      setSignatairesLoading(true);
      setSignatairesError(null);

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/organismes/${commande.organisme.id}/signataires`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur ${response.status} lors de la récupération des signataires`);
        }

        const data = await response.json();
        if (data.status === "success" && Array.isArray(data.data)) {
          console.log("Signataires reçus:", data.data);
          setSignataires(data.data);

          // Transformer les données pour le composant DropDown
          const options = data.data.map(sig => ({
            label: `${sig.prenom} ${sig.nom} (${sig.type_signataire})`,
            value: `${sig.id}|${sig.type_signataire}`
          }));
          setSignatairesOptions(options);

          // Si un signataire est déjà sélectionné dans l'opération
          if (commande.id_signataire && commande.type_signataire) {
            const matchingSignataire = data.data.find(s =>
              s.id === commande.id_signataire && s.type_signataire === commande.type_signataire
            );

            if (matchingSignataire) {
              console.log("Signataire correspondant trouvé:", matchingSignataire);
              setSelectedSignataireId(`${matchingSignataire.id}|${matchingSignataire.type_signataire}`);
            } else if (data.data.length > 0) {
              // Si pas de correspondance mais des signataires disponibles
              setSelectedSignataireId(`${data.data[0].id}|${data.data[0].type_signataire}`);
              // Mettre à jour la commande avec le premier signataire
              updateCommandeSignataire(data.data[0].id, data.data[0].type_signataire);
            }
          } else if (data.data.length > 0) {
            // Aucun signataire sélectionné, prendre le premier
            setSelectedSignataireId(`${data.data[0].id}|${data.data[0].type_signataire}`);
            // Mettre à jour la commande avec le premier signataire
            updateCommandeSignataire(data.data[0].id, data.data[0].type_signataire);
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des signataires:", error);
        setSignatairesError("Impossible de charger la liste des signataires");
      } finally {
        setSignatairesLoading(false);
      }
    };

    fetchSignataires();
  }, [commandeState?.organisme?.id]);

  // Gestion du changement de signataire
  const handleSignataireChange = (value: string) => {
    console.log("Nouveau signataire sélectionné:", value);
    setSelectedSignataireId(value);

    // Extraire l'ID et le type du signataire de la valeur combinée
    const [id, type_signataire] = value.split('|');
    if (id && type_signataire) {
      updateCommandeSignataire(Number(id), type_signataire);
    }
  };

  // Modifié pour passer les informations du signataire correctement
  const handleFinaliserCommandeWithSignataire = () => {
    if (!selectedSignataireId) {
      alert("Veuillez sélectionner un signataire avant de finaliser la commande.");
      return;
    }

    // Récupérer l'ID et le type du signataire sélectionné
    const [id, type_signataire] = selectedSignataireId.split('|');

    console.log("Envoi de la commande avec signataire:", {
      id_signataire: parseInt(id),
      type_signataire: type_signataire
    });

    // Appeler handleFinaliserCommande avec les informations du signataire
    handleFinaliserCommande(parseInt(id), type_signataire);
  };

  const getDisplayStatus = () => {
    const opStatus = commande?.status;
    const cmdStatus = commande?.operation?.status;

    if (!opStatus && !cmdStatus) return 'Statut inconnu';

    const statusName = opStatus?.nom || cmdStatus?.nom;
    const statusId = opStatus?.id || cmdStatus?.id;

    if (isPrestataireView) {
      const rang = getRangPrestataire();
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
    }

    return statusName || 'Statut inconnu';
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande?.operation?.numero || commande?.numero;
    const rang = getRangPrestataire();
    return `Suivi du Marché Subséquent n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande?.operation?.lot_technique || commande?.lot_technique;
    const departement = commande?.operation?.lot_departement?.departement || commande?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  return (<>
    {loading ? (
      <div className="loading-indicator">
        <p>Enregistrement en cours...</p>
      </div>
    ) : (
      <div className="tab-analyse">
        {/* Section 1: Analyse de l'offre technique */}
        <section>
          <h3 className='section-header'>Analyse de l'offre technique</h3>

          <div className="section-content">

            <AnalyseOffreTechnique
              ref={analyseRefValidate}
              prestataires={buildPrestatairesData()}
              techNotes={techNotes}
              setTechNotes={setTechNotes}
              onNext={() => { }}
              viewOnly={commande.status === 'En attente' || viewOnly}
              generatePrestatairePDF={generatePrestatairePDF}
              operation={commande}
            />
          </div>
        </section>

        <div className="page-break" />

        {/* Section 2: Analyse de l'offre financière */}
        <section>
          <h3 className='section-header'>Analyse de l'offre financière</h3>
          <div className="section-content">
            <AnalyseOffreFinanciere subsequentId={commande.id} />
          </div>
        </section>

        <div className="page-break" />

        {/* Section 3: Choix du prestataire */}
        <section>
          <h3 className='section-header'>Synthèse de l'analyse des offres</h3>
          <div className="section-content">
            <SyntheseAnalyse
              prestataires={prepareSyntheseData()}
              selectedPrestataire={selectedPrestataire}
              setSelectedPrestataire={(id) => {
                setSelectedPrestataire(id);
              }}
              onNext={() => { }}
              threeColumnsLayout={true}
              viewOnly={commande.status !== 'En attente' || viewOnly}
              generatePrestatairePDF={generatePrestatairePDF}
              operation={commande}
            />
          </div>
        </section>
        {commande.status.id >= 7 && isAnalyzed && (
          <section>
            <div className="section-content analyze-info">
              <p>Analyse clôturée le {formatedAnalyzeLancee} par {analyzeOperateur}</p>
            </div>
          </section>
        )}


        {/* Boutons d'actions en bas pour la validation finale */}
        <div className="action-buttons no-print">
          {!isViewOnly && (
            <Button
              value={loading ? "Enregistrement..." : "Enregistrer et quitter"}
              variant="secondary"
              onClick={() => {
                if (!analyseRefValidate.current?.validate()) return;
                handleSaveAndQuit();
              }}
            />
          )}

          {/* <Button
            value="PDF"
            icon={<PdfIcon />}
            variant="secondary"
            onClick={() => {
              if (!analyseRefValidate.current?.validate()) return;
              generateGlobalPDF();
            }}
          /> */}
          <PrintButton
            targetRef={printRef}
            fileName={`${commande?.numero || commande?.operation?.numero} - Analyse`}
            suiviHeaderProps={{
              titre: commande?.operation?.nom || commande?.nom,
              sousTitre: getSousTitre(), // fonction disponible dans le parent
              subtitle: getSubtitle(),   // fonction disponible dans le parent
              status: getDisplayStatus(),
              statusColor: getStatusColor(getDisplayStatus()),
              delais: commande?.expire_at || commande?.operation?.expire_at,
              backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
            }}
          />

          {!isViewOnly && (
            <Button
              value={isClosing ? "Clôture de l'analyse en cours" : "Clôturer l'analyse"}
              variant="primary"
              disabled={isClosing} // désactive le bouton
              onClick={() => {
                if (!analyseRefValidate.current?.validate()) return;
                handleClotureConfirm();
              }}
              className={isClosing ? 'disabled-analyse-button' : ''}
            />
          )}
        </div>

        {/* Modal de confirmation pour cloturer l'analyse */}
        <ValidationPopUp
          isOpen={isClotureModalOpen}
          title={"Clôture de l'analyse"}
          text="Vous vous apprêtez à clôturer votre analyse. Il n'est plus possible de modifier votre analyse après cette étape. Souhaitez-vous continuer ?"
          onClose={() => setIsClotureModalOpen(false)}
          onConfirm={handleClotureModalConfirm}
          cancelLabel="Non"
          confirmLabel="Oui"
        />

        {/* Modal de sélection du signataire */}
        <ValidationPopUp
          isOpen={isSignataireModalOpen}
          title={"Choix du signataire"}
          text="Veuillez sélectionner un signataire pour cette commande :"
          onClose={() => setIsSignataireModalOpen(false)}
          onConfirm={handleSignataireModalConfirm}
          cancelLabel="Annuler"
          confirmLabel="Valider"
        >
          {/* Ajout du contenu personnalisé dans la popup */}
          <div className="form-group">
            {signatairesLoading ? (
              <p>Chargement des signataires...</p>
            ) : signatairesError ? (
              <p className="error-message">{signatairesError}</p>
            ) : (
              <>
                <DropDown
                  options={signatairesOptions}
                  value={selectedSignataireId || ''}
                  onChange={handleSignataireChange}
                  variant="bordered"
                  placeholder="Sélectionner un signataire"
                />
                <p>
                  Nous attirons votre attention sur
                  l'importance de l'exactitude du nom, et prénom du signataire qui sera vérifié par notre partenire Yousign
                  pour la signature éléctronique
                </p>
              </>
            )}
            {!selectedSignataireId && (
              <span className="error-message">Veuillez sélectionner un signataire</span>
            )}

          </div>
        </ValidationPopUp>

        {/* Modal finale succès */}
        <ValidationPopUp
          isOpen={isClotureSuccessModalOpen}
          title="Analyse clôturée"
          text="Analyse terminée, il convient maintenant de notifier les décisions et signer le marché. Nous vous invitons à aller dans l’onglet suivant pour finaliser votre démarche dans le délai restant."
          onClose={handleClotureSuccessContinue}
          onConfirm={handleClotureSuccessContinue}
          // cancelLabel=""
          confirmLabel="Continuer"
        />

        {/* Modal d'erreur */}
        <ValidationPopUp
          isOpen={!!clotureErrorModal}
          title="Erreur"
          text={clotureErrorModal || ""}
          onClose={handleClotureErrorClose}
          onConfirm={handleClotureErrorClose}
          // cancelLabel=""
          confirmLabel="Fermer"
        />

        <DetailsNotesModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          critereOrganisation={selectedPrestaDetails ? {
            note: selectedPrestaDetails.note_operationnel || 0,
            commentaire: selectedPrestaDetails.commentaire_operationnel || "",
            description: selectedPrestaDetails.description_operationnel || ""
          } : {
            note: 0,
            commentaire: ""
          }}
          critereMoyen={selectedPrestaDetails ? {
            note: selectedPrestaDetails.note_humain || 0,
            commentaire: selectedPrestaDetails.commentaire_humain || "",
            description: selectedPrestaDetails.description_humain || ""
          } : {
            note: 0,
            commentaire: ""
          }}
          critereSocial={selectedPrestaDetails ? {
            note: selectedPrestaDetails.note_environnement || 0,
            commentaire: selectedPrestaDetails.commentaire_environnement || "",
            description: selectedPrestaDetails.description_environnement || ""
          } : {
            note: 0,
            commentaire: ""
          }}
        />
      </div>
    )}
  </>
  );
};

export default Analyse;